import React, { Fragment } from "react";
import { useHistory } from "react-router-dom";
import { FaTrash, FaWhatsapp, FaShoppingBag } from "react-icons/fa";
import Layout from "../layout";
import { useCart } from "../../../contexts/CartContext";

const apiURL = process.env.REACT_APP_API_URL;

const Cart = () => {
  const history = useHistory();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  const generateWhatsAppMessage = () => {
    if (cartItems.length === 0) return "";

    let message = "Salut Jamaal, je veux commander :\n\n";
    
    cartItems.forEach((item, index) => {
      const offer = parseInt(item.pOffer, 10) || 0;
      const basePrice = Number(item.pPrice) || 0;
      const finalPrice = offer > 0
        ? Math.max(0, Math.round(basePrice * (1 - offer / 100)))
        : basePrice;
      
      const productUrl = `${window.location.origin}/products/${item._id}`;
      
      message += `${index + 1}. ${item.pName}\n`;
      message += `   Quantité: ${item.quantity}\n`;
      message += `   Prix unitaire: ${finalPrice.toLocaleString()} FCFA\n`;
      message += `   Sous-total: ${(finalPrice * item.quantity).toLocaleString()} FCFA\n`;
      message += `   Lien: ${productUrl}\n\n`;
    });

    const total = getCartTotal();
    message += `📦 TOTAL: ${total.toLocaleString()} FCFA`;

    return encodeURIComponent(message);
  };

  const handleWhatsAppOrder = () => {
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/221777336109?text=${message}`;
    window.open(whatsappUrl, '_blank');
    // Optionnel: Vider le panier après commande
    // clearCart();
  };

  if (cartItems.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 min-h-screen">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8 bg-gray-50 rounded-full w-40 h-40 mx-auto flex items-center justify-center">
              <FaShoppingBag className="w-20 h-20 text-gray-300" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Votre panier est vide
            </h1>
            <p className="text-gray-600 mb-8 text-lg">
              Découvrez nos produits et ajoutez-les à votre panier !
            </p>
            <button
              onClick={() => history.push("/")}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Continuer mes achats
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-blue-300 min-h-screen py-8 md:py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-blue-800 mb-2">
              Mon Panier
            </h1>
            <p className="text-gray-600">
              {cartItems.length} {cartItems.length > 1 ? 'produits' : 'produit'} dans votre panier
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Liste des produits */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => {
                const offer = parseInt(item.pOffer, 10) || 0;
                const basePrice = Number(item.pPrice) || 0;
                const finalPrice = offer > 0
                  ? Math.max(0, Math.round(basePrice * (1 - offer / 100)))
                  : basePrice;

                return (
                  <div
                    key={item._id}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 p-4 md:p-6 border border-gray-100"
                  >
                    <div className="flex gap-4">
                      {/* Image */}
                      <div
                        className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0 cursor-pointer border border-gray-200"
                        onClick={() => history.push(`/products/${item._id}`)}
                      >
                        <img
                          src={`${apiURL}/uploads/products/${item.pImages?.[0]}`}
                          alt={item.pName}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>

                      {/* Infos */}
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <h3
                            className="text-lg md:text-xl font-bold text-gray-900 mb-2 cursor-pointer hover:text-blue-600 transition-colors line-clamp-2"
                            onClick={() => history.push(`/products/${item._id}`)}
                          >
                            {item.pName}
                          </h3>
                          
                          {/* Prix */}
                          <div className="flex items-center gap-3 mb-4">
                            {offer > 0 && (
                              <span className="text-sm text-gray-400 line-through">
                                {basePrice.toLocaleString()} F
                              </span>
                            )}
                            <span className="text-2xl font-bold text-gray-900">
                              {finalPrice.toLocaleString()} F
                            </span>
                            {offer > 0 && (
                              <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-md font-semibold">
                                -{offer}%
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                          {/* Quantité */}
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
                            <button
                              onClick={() => updateQuantity(item._id, item.quantity - 1)}
                              className="w-9 h-9 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center font-bold text-gray-700 transition-colors shadow-sm border border-gray-200"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                              </svg>
                            </button>
                            <span className="w-12 text-center font-bold text-gray-900 text-lg">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item._id, item.quantity + 1)}
                              className="w-9 h-9 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center font-bold text-gray-700 transition-colors shadow-sm border border-gray-200"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          </div>

                          {/* Supprimer */}
                          <button
                            onClick={() => removeFromCart(item._id, item.pName)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors group"
                            title="Supprimer"
                          >
                            <FaTrash className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Résumé */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-4 border-b border-gray-200">
                  Résumé
                </h2>

                {/* Détails */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-700">
                    <span className="font-medium">Sous-total</span>
                    <span className="font-semibold">{getCartTotal().toLocaleString()} F</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span className="font-medium">Articles</span>
                    <span className="font-semibold">
                      {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                    </span>
                  </div>
                </div>

                {/* Total */}
                <div className="border-t border-gray-200 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-gray-900">Total</span>
                    <span className="text-3xl font-black text-blue-600">
                      {getCartTotal().toLocaleString()} F
                    </span>
                  </div>
                </div>

                {/* Boutons */}
                <div className="space-y-3">
                  <button
                    onClick={handleWhatsAppOrder}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <FaWhatsapp className="w-6 h-6" />
                    Commander via WhatsApp
                  </button>

                  <button
                    onClick={() => history.push("/")}
                    className="w-full px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-blue-600 hover:text-blue-600 transition-all duration-300"
                  >
                    Continuer mes achats
                  </button>

                  <button
                    onClick={clearCart}
                    className="w-full px-6 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors"
                  >
                    Vider le panier
                  </button>
                </div>

                {/* Info WhatsApp */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-xs text-gray-700">
                      <p className="font-semibold mb-1 text-blue-900">Commande rapide</p>
                      <p>Votre commande sera envoyée directement à notre équipe via WhatsApp pour une confirmation instantanée.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Cart;
